import type { CatalogEntry } from '../types';

/**
 * Tags (feature `13`) error codes + catalog.
 *
 * Code shape is `13AAEE` (decimal, zero-padded):
 *   - `AA` = endpoint within the feature (`00` = feature-level / cross-endpoint).
 *   - `EE` = specific condition within that endpoint.
 *
 * `Shared` codes (`1300xx`, AA=00) are recurring cross-endpoint conditions —
 * `TagLimitReached` (per-workspace cap) — collapsed to ONE code so the same
 * condition reads the same on every tag-touching route.
 *
 * Endpoint → `AA` map:
 *   createTag=01, setAccountTags=02 (PUT /email-accounts/:id/tags),
 *   bulkApplyTags=03 (POST /email-accounts/bulk/tags).
 *
 * `setAccountTags` and `bulkApplyTags` share the per-account cap condition via
 * `AssignTags.TooManyTagsPerAccount` (one condition, one code) and reuse the
 * `Create.*` name-validation codes when they create-on-assign.
 */
export const TAGS_CODES = {
  /** Cross-endpoint conditions (AA=00). */
  Shared: {
    TagLimitReached: '130002',
  },
  /** createTag=01 (and create-on-assign name validation). */
  Create: {
    NameInvalid: '130101',
    NameTaken: '130102',
  },
  /** Tag-assignment endpoints (setAccountTags=02, bulkApplyTags=03). */
  AssignTags: {
    TooManyTagsPerAccount: '130201',
  },
} as const;

export const TAGS_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared (cross-endpoint) ──
  '130002': {
    httpStatus: 409,
    message: "You've reached the tag limit for this workspace.",
  },

  // ── Create / create-on-assign name validation ──
  '130101': {
    httpStatus: 400,
    message: 'Tag names must be between 1 and 50 characters.',
  },
  '130102': { httpStatus: 409, message: 'A tag with that name already exists.' },

  // ── Tag assignment ──
  '130201': {
    httpStatus: 409,
    message: 'An account can have at most 25 tags.',
  },
};
